// --- Our Modules --------------------------------------------------------------------------------
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    label: string;
    value: number;
    onChange: (value: number) => void;
    /** What each step is worth, said beside the label — "5 GB · 240 SAR", and never built by hand. */
    hint?: string;
    min?: number;
    /** Left out on purpose where there is no product rule: the API's own ceiling is the only limit. */
    max?: number;
    disabled?: boolean;
};

/**
 * A number with two buttons, the shape the client's own storage request uses — and the reason it is
 * here is that the admin's form offered a **dropdown of five numbers**. Five was never a rule: an
 * admin writing a row by hand is recording a conversation that has already happened, and a company
 * buying forty packs could not be written down at all.
 *
 * It is typable as well as steppable, because stepping to forty is forty presses.
 */
export default function StepperInput({ label, value, onChange, hint, min = 0, max, disabled }: Props) {
    const clamp = (next: number) => Math.max(min, max === undefined ? next : Math.min(max, next));

    return (
        <div className="stepper">
            <div className="stepper__text">
                <span className="stepper__label">{label}</span>
                {hint && <span className="stepper__hint">{hint}</span>}
            </div>

            <div className="stepper__control">
                <button
                    type="button"
                    className="stepper__step"
                    onClick={() => onChange(clamp(value - 1))}
                    disabled={disabled || value <= min}
                    aria-label="-"
                >
                    −
                </button>

                <input
                    className="stepper__value"
                    value={String(value)}
                    onChange={(event) => onChange(clamp(parseInt(event.target.value.replace(/\D/g, "")) || 0))}
                    inputMode="numeric"
                    disabled={disabled}
                    aria-label={label}
                />

                <button
                    type="button"
                    className="stepper__step"
                    onClick={() => onChange(clamp(value + 1))}
                    disabled={disabled || (max !== undefined && value >= max)}
                    aria-label="+"
                >
                    +
                </button>
            </div>
        </div>
    );
}
